import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useIsNarrow } from '../../hooks/useIsNarrow'
import { useLibraryDeps } from '../../library/LibraryDeps'
import { useToast } from '../../library/useToast'
import { createOfApi } from '../ofApi'
import { ErrorLine, Loading } from '../settings/kit'
import { useLoad } from '../settings/kitHooks'
import type { MorePageProps } from './morePage'
import { CreateEditStream } from './streaming/CreateEditStream'
import { Dashboard } from './streaming/Dashboard'
import type { StreamingCtx } from './streaming/context'
import { DASHBOARD_REFERER, parseMe, parseView } from './streaming/streamingData'
import '../../library/library.css'
import '../ofPages.css'
import '../settings/kit.css'
import './streaming/streaming.css'

/**
 * Streaming: onlyfans.com/my/streaming/dashboard (captured 2026-10-22) — past
 * live streams with their statistics, scheduling a stream (`view=create`) and
 * editing one (`edit=<id>`), as OnlyFans' own page calls OnlyFans. Going live
 * itself happens on onlyfans.com.
 */
export function StreamingPage({ accountId, canWrite, query, onOpenPage, mobileNav }: MorePageProps) {
  const { t } = useTranslation('ofMore', { keyPrefix: 'streamingPage' })
  const { gateway } = useLibraryDeps()
  const of = useMemo(() => createOfApi(gateway), [gateway])
  const mobile = useIsNarrow()
  const toast = useToast()
  const view = parseView(query)
  const me = useLoad(async () => parseMe(await of.get('users/me', {}, DASHBOARD_REFERER)), [of, accountId])

  const go = useCallback((q: string, replace = false) => onOpenPage('streaming', q, replace ? { replace: true } : undefined), [onOpenPage])

  let body
  if (me.data) {
    const ctx: StreamingCtx = { accountId, of, me: me.data, canWrite, mobile, go, openPage: onOpenPage, say: toast.say }
    body = view.kind === 'dashboard' ? <Dashboard ctx={ctx} /> : <CreateEditStream key={view.kind === 'edit' ? view.id : 'new'} ctx={ctx} view={view} />
  } else if (me.loading) {
    body = <Loading />
  } else {
    body = (
      <ErrorLine onRetry={me.reload}>
        {t('loadFailed', { error: me.error || '—' })}
      </ErrorLine>
    )
  }

  return (
    <div className={`lib oset ofst ${mobile ? 'lib--m ofst--m' : ''}`}>
      <main className="lib-main">
        <div className="ofst-scroll">{body}</div>
        {toast.node}
      </main>
      {mobile && mobileNav}
    </div>
  )
}
